import { Link } from "react-router-dom";
import { Truck, Package, Undo2, Mail } from "lucide-react";

export default function Policies() {
    return (
        <div className="relative z-10 pt-24 sm:pt-32 pb-24" data-testid="policies-page">
            <div className="max-w-[900px] mx-auto px-6 sm:px-10">
                <div className="mb-16 fade-up">
                    <div className="label-caps text-[#8C3A3A] mb-4">Studio Policy</div>
                    <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter text-[#1A1A1A] leading-none mb-8">
                        Shipping<br /><span className="italic text-[#5A5A5A]">& returns.</span>
                    </h1>
                    <p className="text-[#5A5A5A] leading-relaxed max-w-2xl">
                        Every work leaves the studio in Australia — prints are made-to-order and dispatched worldwide,
                        originals are personally packed, signed, and insured. Here is exactly what to expect.
                    </p>
                </div>

                <div className="space-y-16">
                    {/* Processing */}
                    <section data-testid="policy-processing">
                        <div className="flex items-center gap-3 mb-6">
                            <Package className="w-4 h-4 text-[#8C3A3A]" />
                            <h2 className="label-caps text-[#1A1A1A]">Processing Time</h2>
                        </div>
                        <div className="prose text-[#5A5A5A] leading-relaxed space-y-4 text-base">
                            <p>
                                <strong className="text-[#1A1A1A]">Fine art prints</strong> are printed to order and
                                typically ship within <strong className="text-[#1A1A1A]">3 – 5 business days</strong>{" "}
                                (AEST) of your order being confirmed.
                            </p>
                            <p>
                                <strong className="text-[#1A1A1A]">Original works</strong> are personally packed by the
                                artist and typically ship within <strong className="text-[#1A1A1A]">5 – 10 business
                                days</strong>. Larger canvases and framed pieces may require additional preparation —
                                you will be notified directly if this is the case.
                            </p>
                        </div>
                    </section>

                    {/* Shipping */}
                    <section className="border-t border-black/[0.06] pt-16" data-testid="policy-shipping">
                        <div className="flex items-center gap-3 mb-6">
                            <Truck className="w-4 h-4 text-[#8C3A3A]" />
                            <h2 className="label-caps text-[#1A1A1A]">Shipping & Delivery</h2>
                        </div>
                        <div className="prose text-[#5A5A5A] leading-relaxed space-y-4 text-base">
                            <p>
                                We ship to Australia and internationally. Estimated transit times from dispatch:
                            </p>
                            <ul className="list-disc pl-6 space-y-2">
                                <li><strong className="text-[#1A1A1A]">Within Australia:</strong> 3 – 7 business days</li>
                                <li><strong className="text-[#1A1A1A]">New Zealand:</strong> 5 – 10 business days</li>
                                <li><strong className="text-[#1A1A1A]">United States, Canada, UK, Europe:</strong> 7 – 14 business days</li>
                                <li><strong className="text-[#1A1A1A]">Rest of world:</strong> 10 – 21 business days</li>
                            </ul>
                            <p>
                                Prints ship flat or rolled in a rigid tube depending on size. Originals ship framed
                                where indicated, fully insured for their sale value.
                            </p>
                            <p>
                                <strong className="text-[#1A1A1A]">Tracking:</strong> A tracking number is emailed to you
                                as soon as the parcel is scanned by the carrier.
                            </p>
                            <p className="text-sm text-[#8A8A8A]">
                                Import duties, taxes, and customs fees on international orders are the responsibility
                                of the customer.
                            </p>
                        </div>
                    </section>

                    {/* Returns */}
                    <section className="border-t border-black/[0.06] pt-16" data-testid="policy-returns">
                        <div className="flex items-center gap-3 mb-6">
                            <Undo2 className="w-4 h-4 text-[#8C3A3A]" />
                            <h2 className="label-caps text-[#1A1A1A]">Returns & Refunds</h2>
                        </div>
                        <div className="prose text-[#5A5A5A] leading-relaxed space-y-4 text-base">
                            <p>
                                Because every print is made to order and originals are one-of-one, we do not accept
                                returns for change of mind. We will, however, always make it right if something has
                                gone wrong on our end.
                            </p>
                            <p>
                                <strong className="text-[#1A1A1A]">Damaged in transit or incorrect item</strong> —
                                contact us within <strong className="text-[#1A1A1A]">14 days</strong> of delivery with
                                photos of the item and packaging. We will replace, reprint, or refund at the artist's
                                discretion, no questions asked.
                            </p>
                            <p>
                                <strong className="text-[#1A1A1A]">Refunds</strong> are issued to your original payment
                                method within 5 – 10 business days of the resolution being agreed.
                            </p>
                            <p>
                                <strong className="text-[#1A1A1A]">Commissions</strong> are governed by their individual
                                agreement and deposit terms.
                            </p>
                        </div>
                    </section>

                    {/* Contact */}
                    <section className="border-t border-black/[0.06] pt-16" data-testid="policy-contact">
                        <div className="flex items-center gap-3 mb-6">
                            <Mail className="w-4 h-4 text-[#8C3A3A]" />
                            <h2 className="label-caps text-[#1A1A1A]">Questions About Your Order?</h2>
                        </div>
                        <p className="text-[#5A5A5A] leading-relaxed text-base mb-6">
                            The studio inbox is monitored personally by Nicholas. Reach out anytime.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <a
                                href="mailto:art.nickmoore@gmail.com"
                                className="inline-flex items-center gap-3 bg-[#1A1A1A] text-[#FAFAF7] px-8 py-4 rounded-full text-sm tracking-wide font-medium hover:bg-[#8C3A3A] transition-colors"
                                data-testid="policy-email-btn"
                            >
                                <Mail className="w-4 h-4" /> art.nickmoore@gmail.com
                            </a>
                            <Link
                                to="/about"
                                className="inline-flex items-center gap-3 border border-black/20 text-[#1A1A1A] px-8 py-4 rounded-full text-sm tracking-wide hover:bg-black/5 transition-colors"
                                data-testid="policy-about-link"
                            >
                                Full contact details
                            </Link>
                        </div>
                    </section>
                </div>

                <div className="mt-24 pt-8 border-t border-black/[0.06] text-xs text-[#8A8A8A] tracking-widest uppercase">
                    Last updated {new Date().toLocaleDateString("en-AU", { month: "long", year: "numeric" })}
                </div>
            </div>
        </div>
    );
}
